import { isTrustedRedirect, redirect } from '~/lib/navigation';

import { client } from './client';

export const PlaidParams = {
  response_type: undefined,
  redirect_uri: undefined,
  scope: undefined,
  client_id: undefined,
  state: undefined,
} as const;

export type PlaidParamsType = Record<keyof typeof PlaidParams, string | undefined>;

export const PLAID_FLOW_STARTED_AT_PARAM = 'flow_started_at';
export const PLAID_FLOW_CAPTURED_AT_PARAM = 'flow_captured_at';

export const PLAID_AUTH_FLOW_TTL_MS = 30 * 60 * 1000;

export const PLAID_AUTH_FLOW_MAX_AGE_MS = 60 * 60 * 1000;

const PLAID_OAUTH_RETURN_URL = 'https://cdn.plaid.com/link/v2/stable/oauth.html';

interface GetTempCodeResponse {
  platformId: string;
  dashboardUserId: string;
  tempCode: string;
}

export type PlaidErrorType =
  | 'invalid_request'
  | 'unauthorized_client'
  | 'access_denied'
  | 'unsupported_response_type'
  | 'invalid_scope'
  | 'server_error'
  | 'temporarily_unavailable';

export interface PlaidRedirectUrlType {
  code?: string;
  error?: PlaidErrorType;
}

export const capturePlaidAuthFlowParams = (search: URLSearchParams): Record<string, string | undefined> | undefined => {
  if (!search.get('redirect_uri') || !search.get('state')) {
    return undefined;
  }

  const captured = Object.keys(PlaidParams).filter((key) => search.has(key));

  return {
    ...Object.fromEntries(captured.map((key) => [key, search.get(key) ?? undefined])),
    [PLAID_FLOW_CAPTURED_AT_PARAM]: String(Date.now()),
  };
};

const isFresh = (value: string | undefined, maxAgeMs: number): boolean => {
  const at = Number(value);
  return Number.isFinite(at) && at > 0 && Date.now() - at <= maxAgeMs;
};

export const stampPlaidAuthFlow = (
  params?: Record<string, string | undefined>
): Record<string, string | undefined> | undefined => {
  if (!getPlaidAuthFlow(params)) {
    return undefined;
  }

  const now = String(Date.now());
  return { ...params, [PLAID_FLOW_CAPTURED_AT_PARAM]: now, [PLAID_FLOW_STARTED_AT_PARAM]: now };
};

export const getPlaidAuthFlow = (params?: Record<string, string | undefined>): PlaidParamsType | undefined =>
  params?.redirect_uri && params?.state && isFresh(params[PLAID_FLOW_CAPTURED_AT_PARAM], PLAID_AUTH_FLOW_MAX_AGE_MS)
    ? (params as PlaidParamsType)
    : undefined;

export const getActivePlaidAuthFlow = (params?: Record<string, string | undefined>): PlaidParamsType | undefined => {
  if (!getPlaidAuthFlow(params)) {
    return undefined;
  }

  if (!isFresh(params?.[PLAID_FLOW_STARTED_AT_PARAM], PLAID_AUTH_FLOW_TTL_MS)) {
    return undefined;
  }

  return params as PlaidParamsType;
};

const parsePlaidReturnUrl = (redirectURI?: string): URL | undefined => {
  if (!redirectURI) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(redirectURI);
  } catch {
    return undefined;
  }

  const expected = new URL(PLAID_OAUTH_RETURN_URL);
  if (url.origin !== expected.origin || url.pathname !== expected.pathname) {
    return undefined;
  }

  return isTrustedRedirect(url.toString(), { trustedURL: PLAID_OAUTH_RETURN_URL }) ? url : undefined;
};

export const canReturnToPlaid = (params: PlaidParamsType): boolean =>
  !!params.state && !!parsePlaidReturnUrl(params.redirect_uri);

export const handlePlaidRedirectUrl = (params: PlaidParamsType, object: PlaidRedirectUrlType): boolean => {
  const url = parsePlaidReturnUrl(params.redirect_uri);
  if (!url || !params.state) {
    return false;
  }

  url.searchParams.set('state', params.state);
  if (object.error) {
    url.searchParams.set('error', object.error);
  }
  if (object.code) {
    url.searchParams.set('code', object.code);
  }

  return redirect(url.toString(), { trustedURL: PLAID_OAUTH_RETURN_URL });
};

export class PlaidRepository {
  static async getTempCode(platformId?: string) {
    return client.post<object, GetTempCodeResponse>(
      '/plaid/auth/oauth2/temp-code',
      {},
      {
        sandbox: false,
        platformId,
      }
    );
  }
}
